import { hasAnyRole, Roles, SchedulePublishers, type Role } from "../../auth/roles";
import { screensFor } from "../hub/hubs";

// The guided tour's content (owner, 2026-10-22: "a take-the-tour that will work for all the roles"; D-O115): one shared
// part over the header and the shell, then a role chapter picked from the roles the person was ASSIGNED. Several roles:
// the highest chapter leads, and the last card offers the others ("More for your other roles"). The chapter table mirrors
// the server's TourChapterRules (same cases in both test suites). Every step names a page the person may open — a step is
// left out when the menu would not show that page — and says in one or two plain sentences what they can DO there.

export interface TourStep {
  id: string;
  /** CSS selectors tried in order; the first visible one is highlighted. */
  targets: string[];
  title: string;
  body: string;
  /** The page to open first. Omitted: the step is in the header or the shell, on whatever page is open. */
  route?: string;
  /** Already on the right page when the path matches (a detail page reached by `follow`). */
  routeMatch?: RegExp;
  /** A link on the current page to follow to reach this step's page (the first request in the queue). */
  follow?: string;
  /** Left out unless the person's menu has this page. */
  screen?: string;
  /** Left out unless the person holds one of these roles (an act behind a narrower policy than its page). */
  roles?: readonly string[];
  /** The closing card that offers the other chapters. */
  kind?: "more";
}

export type TourChapterId = "admin" | "plant-manager" | "operations-manager" | "planner" | "purchasing" | "supervisor" | "viewer";

/** Highest first (server: TourChapterRules.Priority). */
export const CHAPTER_PRIORITY: readonly TourChapterId[] = ["admin", "plant-manager", "operations-manager", "planner", "purchasing", "supervisor", "viewer"];

export const CHAPTER_FOR_ROLE: Readonly<Record<string, TourChapterId>> = {
  [Roles.Admin]: "admin",
  [Roles.ITAdministrator]: "admin",
  [Roles.PlantManager]: "plant-manager",
  [Roles.OperationsManager]: "operations-manager",
  [Roles.Planner]: "planner",
  [Roles.MasterScheduler]: "planner",
  [Roles.Purchasing]: "purchasing",
  [Roles.PurchasingManager]: "purchasing",
  [Roles.ShopSupervisor]: "supervisor",
  [Roles.OperationsViewer]: "viewer",
  [Roles.Executive]: "viewer",
  [Roles.Quality]: "viewer",
  [Roles.QualityApprover]: "viewer",
  [Roles.EngineeringManager]: "viewer",
  [Roles.ShippingManager]: "viewer",
  [Roles.Logistics]: "viewer"
};

export const CHAPTER_LABELS: Readonly<Record<TourChapterId, string>> = {
  admin: "Admin",
  "plant-manager": "Plant Manager",
  "operations-manager": "Operations Manager",
  planner: "Planner and Master Scheduler",
  purchasing: "Purchasing",
  supervisor: "Shop Supervisor",
  viewer: "Looking around"
};

export interface ChapterSelection {
  primary: TourChapterId | null;
  others: TourChapterId[];
}

/** The lead chapter and the others, from the roles the person was assigned (the Planner ⇄ OM equivalence excluded). */
export function selectChapters(assignedRoles: readonly string[] | undefined): ChapterSelection {
  const held = new Set((assignedRoles ?? []).map((r) => CHAPTER_FOR_ROLE[r]).filter((c): c is TourChapterId => !!c));
  const ordered = CHAPTER_PRIORITY.filter((c) => held.has(c));
  return { primary: ordered[0] ?? null, others: ordered.slice(1) };
}

// ── The shared part ──────────────────────────────────────────────────────────────────────────────────────────────────

export const SHARED_STEPS: readonly TourStep[] = [
  {
    id: "home",
    route: "/",
    targets: ['[data-testid="start-view"]', '[data-testid="home-page"]', "#main h1", "main h1"],
    title: "Your start page",
    body: "Every sign-in starts here, with the things your role works on first. Select a card to open it."
  },
  {
    id: "search",
    targets: ['[data-tour="search"]', '[data-testid="phone-tab-search"]'],
    title: "Search anything",
    body: "Type a work order (600160), a part, a customer, a machine or a purchase order to open it. Ctrl+K opens search from anywhere."
  },
  {
    id: "goto",
    targets: ['[data-testid="goto-button"]'],
    title: "Go to",
    body: "Ctrl+P lists every page you may open, a few quick actions and what you opened recently. Type a few letters and press Enter."
  },
  {
    id: "chip",
    targets: ['[data-testid="sync-chip"]'],
    title: "How old the data is",
    body: "This tells you in words when the numbers were last read from GSS. Open it to see each source or to refresh the page."
  },
  {
    id: "notifications",
    targets: ['[data-testid="notification-bell"]', '[data-testid="phone-tab-notifications"]'],
    title: "Your notifications",
    body: "What changed for you, in one place. Open one to go straight to it, and choose how each kind reaches you."
  },
  {
    id: "ticker",
    targets: ['[data-testid="ticker-mount"]'],
    title: "What needs attention",
    body: "The ticker brings the things your role should act on today. Select one to open it."
  },
  {
    id: "hubs",
    targets: ['[data-testid="menu-button"]', "#app-sidebar nav"],
    title: "Your menu",
    body: "Only the hubs your role may use. Each hub has its pages along the top of the page."
  },
  {
    id: "viper",
    targets: ['[data-testid="viper-launcher"]'],
    title: "Ask VIPER",
    body: "Ask a question in plain words, such as \"what is late in the machine shop?\". VIPER explains and suggests; it never changes the schedule or anything else."
  },
  {
    id: "feedback",
    targets: ['[data-testid="pilot-feedback-button"]'],
    title: "Pilot feedback",
    body: "During the pilot, press this to tell us what works and what does not. A picture of the page goes with it, so we see what you saw."
  },
  {
    id: "phone",
    targets: ['[data-testid="phone-tab-scan"]', '[data-testid="user-menu"]'],
    title: "On your phone",
    body: "Open the same address on your phone. Scan reads a router label with the camera and opens its work order; the account menu switches between phone and desktop layouts."
  },
  {
    id: "account",
    targets: ['[data-testid="user-menu"]'],
    title: "Your account",
    body: "Take this tour again, turn on larger text or reduce motion, see the keyboard shortcuts, and sign out."
  }
];

// ── The role chapters ────────────────────────────────────────────────────────────────────────────────────────────────

const HUB_TOP = ['[data-testid="hub-analytics-kpi-strip"]', '[data-testid="kpi-strip"]', '[data-testid="health-tiers"]'];
const REQUEST_PAGE = /^\/purchasing\/requests\/[^/]+\/?$/;
const FIRST_REQUEST = '[data-testid="purchasing-workspace-queue-table"] a[href^="/purchasing/requests/"]:not([href$="/router"])';

export const CHAPTER_STEPS: Readonly<Record<TourChapterId, readonly TourStep[]>> = {
  "plant-manager": [
    {
      id: "pm-hub",
      route: "/operations",
      screen: "/operations",
      targets: HUB_TOP,
      title: "The plant at a glance",
      body: "The top row shows on-time delivery, output, WIP age and the bottleneck. Select a figure to see the work behind it."
    },
    {
      id: "pm-flow",
      route: "/operations",
      screen: "/operations",
      targets: ['[data-testid="plant-flow-summary"]', '[data-testid="plant-flow-sheet"]'],
      title: "How work flows",
      body: "The plant flow shows where work is waiting between the shops. Select a stage to see its work orders."
    },
    {
      id: "pm-approvals",
      route: "/quality/approvals",
      screen: "/quality/approvals",
      targets: ['[data-testid="approvals-queue-table"]', '[data-testid="approvals-page"]'],
      title: "Final approvals",
      body: "Controlled documents come here for your final approval. Approving is your signature, so you confirm it is you."
    },
    {
      id: "pm-customers",
      route: "/customers",
      screen: "/customers",
      targets: ['[data-testid="customers-summary-strip"]', '[data-testid="customers-list-page"]'],
      title: "Customers and on-time delivery",
      body: "See which customers are healthy, on watch or at risk. Open one to see its late orders and what is being done."
    },
    {
      id: "pm-brief",
      route: "/viper/brief",
      screen: "/viper/brief",
      targets: ['[data-testid="morning-brief-kpi-strip"]', '[data-testid="morning-brief-page"]'],
      title: "Morning brief",
      body: "Yesterday's output, today's risks and outside processing on one page. Print it for the morning meeting."
    }
  ],
  "operations-manager": [
    {
      id: "om-hub",
      route: "/operations",
      screen: "/operations",
      targets: HUB_TOP,
      title: "Your hub",
      body: "Start here each morning: on-time delivery, today's slips and the bottleneck. Select anything to see the work behind it."
    },
    {
      id: "om-hotlist",
      route: "/shop-floor/hotlist",
      screen: "/shop-floor/hotlist",
      targets: ['[data-testid="hotlist-entries-table"]', '[data-testid="hotlist-page"]'],
      title: "Hotlist and priorities",
      body: "Put a work order on the hotlist with a reason and a review date. The shops see it at the top of their dispatch lists."
    },
    {
      id: "om-floor",
      route: "/floor-command",
      screen: "/floor-command",
      targets: ['[data-testid="floor-command-wall-toggle"]', '[data-testid="floor-command-page"]'],
      title: "Floor Command and the wall",
      body: "Every workcenter, who is working and what is late, kept up to date. Wall view turns it into a big-screen board for the shop."
    },
    {
      id: "om-approvals",
      route: "/quality/approvals",
      screen: "/quality/approvals",
      targets: ['[data-testid="approvals-queue-table"]', '[data-testid="approvals-page"]'],
      title: "Approvals",
      body: "Documents waiting for you. Each approval is your signature, so you confirm it is you."
    },
    {
      id: "om-assign",
      route: "/people/assignments",
      screen: "/people/assignments",
      targets: ['[data-testid="assignments-operations-table"]', '[data-testid="assignments-kpi-strip"]'],
      title: "Assign work",
      body: "Choose operations and give them to a person. You can undo a batch straight after."
    },
    {
      id: "om-weekly",
      route: "/operations/weekly-report",
      screen: "/operations/weekly-report",
      targets: ['[data-testid="weekly-headline"]', '[data-testid="weekly-kpi-grid"]'],
      title: "The weekly report",
      body: "The week on one page, ready to share. Export it to Excel from the top of the page."
    }
  ],
  planner: [
    {
      id: "pl-master",
      route: "/planning/master",
      screen: "/planning/master",
      targets: ['[data-testid="master-tiles"]', '[data-testid="master-schedule-page"]'],
      title: "Master schedule",
      body: "The top-level orders, their constraints and the frozen zone. Check the plan here before it goes out."
    },
    {
      id: "pl-gantt",
      route: "/planning/orders",
      screen: "/planning/orders",
      targets: ['[data-testid="gantt-filter-bar"]', '[data-testid="gantt-filters"]', '[data-testid="gantt-toolbar"]'],
      title: "Order Gantt and its filters",
      body: "Filter by shop, workcenter, late or hot orders to find what matters. Select a bar to see its operations."
    },
    {
      id: "pl-saved",
      route: "/planning/orders",
      screen: "/planning/orders",
      targets: ['[data-testid="saved-filters-button"]'],
      title: "Saved filters",
      body: "Save the filters you use every day and pick them again in one click. They are kept for you."
    },
    {
      id: "pl-rebalance",
      route: "/planning/orders",
      screen: "/planning/scenarios",
      targets: ['[data-testid="gantt-rebalance"]', '[data-testid="rebalance-open"]'],
      title: "Rebalance",
      body: "Rebalance asks the scheduler for a better plan as a what-if. Nothing applies until you accept it."
    },
    {
      id: "pl-scenarios",
      route: "/planning/scenarios",
      screen: "/planning/scenarios",
      targets: ['[data-testid="scenario-table"]', '[data-testid="planning-scenario-lab"]'],
      title: "Scenario lab",
      body: "Try changes in a scenario and compare it with today's plan. Nothing applies until you accept it."
    },
    {
      id: "pl-publish",
      route: "/planning/master",
      screen: "/planning/master",
      roles: SchedulePublishers,
      targets: ['[data-testid="master-publish-open"]', '[data-testid="master-baseline"]'],
      title: "Publishing is a signature",
      body: "When the plan is right, publish it as the new baseline. Publishing is your signature, so you confirm it is you."
    }
  ],
  purchasing: [
    {
      id: "pu-queue",
      // D-O234: the router queue is the workspace's Work-order routers view, opened explicitly (the tour
      // compares paths without the query, and the person may be on another tab).
      route: "/purchasing?tab=routers",
      screen: "/purchasing",
      targets: ['[data-testid="purchasing-workspace-queue-table"]', '[data-testid="purchasing-workspace-page"]'],
      title: "Your purchasing queue",
      body: "Every outside-processing request waiting for you. Filter by status, vendor or shop, then open one."
    },
    {
      id: "pu-router",
      screen: "/purchasing",
      routeMatch: REQUEST_PAGE,
      follow: FIRST_REQUEST,
      targets: ['[data-testid="purchasing-router-number"]', '[data-testid="purchasing-request-summary"]'],
      title: "The router",
      body: "Each request has its router, numbered SO-RTR-…. Open the router to see every line the vendor will get."
    },
    {
      id: "pu-gss",
      screen: "/purchasing",
      routeMatch: REQUEST_PAGE,
      targets: ['[data-testid="purchasing-changes-panel"]'],
      title: "Check it against GSS",
      body: "This shows what changed in GSS since the request was sent: due dates, quantities, revision and outside operations."
    },
    {
      id: "pu-approve",
      screen: "/purchasing",
      routeMatch: REQUEST_PAGE,
      roles: [Roles.Purchasing, Roles.PurchasingManager],
      targets: ['[data-testid="purchasing-approve-button"]'],
      title: "Approve (signed)",
      body: "When the router is right, approve it. Approving is your signature, so you confirm it is you."
    },
    {
      id: "pu-print",
      screen: "/purchasing",
      routeMatch: REQUEST_PAGE,
      targets: ['[data-testid="purchasing-print-router-button"]', '[data-testid="purchasing-download-docx"]'],
      title: "Print or Word copy",
      body: "Print the router for the vendor, or download a Word copy to send by email."
    },
    {
      id: "pu-osp",
      screen: "/purchasing",
      routeMatch: REQUEST_PAGE,
      targets: ['[data-testid="purchasing-osp-tracking-table"]'],
      title: "Track outside processing",
      body: "Record the PO number, vendor, ship-out date and promised return for each outside operation."
    },
    {
      id: "pu-late",
      route: "/purchasing?tab=watch",
      screen: "/purchasing",
      targets: ['[data-testid="purchasing-late-pos-table"]', '[data-testid="purchasing-page"]'],
      title: "Late purchase orders",
      body: "Purchase orders past their promised date, so you know who to chase first."
    },
    {
      id: "pu-email",
      route: "/purchasing?tab=routers",
      screen: "/purchasing",
      targets: ['[data-testid="purchasing-workspace-kpis"]', '[data-testid="purchasing-workspace-counts"]', '[data-testid="purchasing-workspace-page"]'],
      title: "Your email",
      body: "When Engineering closes a work order that needs outside processing, you get an email with a link straight to its request here."
    }
  ],
  supervisor: [
    {
      id: "su-dispatch",
      route: "/shop-floor/dispatch",
      screen: "/shop-floor/dispatch",
      targets: ['[data-testid="dispatch-table"]', '[data-testid="dispatch-page"]'],
      title: "Dispatch list",
      body: "What to run next at each workcenter, in order. Print it or show it on a wall screen."
    },
    {
      id: "su-shop",
      route: "/shop-floor/dispatch",
      screen: "/shop-floor/dispatch",
      targets: ['[data-testid="dispatch-filter-shop"]'],
      title: "Your shop",
      body: "Pick your shop to see only your workcenters."
    },
    {
      id: "su-material",
      route: "/shop-floor/material",
      screen: "/shop-floor/material",
      targets: ['[data-testid="launch-board-table"]', '[data-testid="material-page"]'],
      title: "Material readiness",
      body: "Before a job starts, check its material and outside processing are ready."
    },
    {
      id: "su-problem",
      route: "/knowledge/incidents",
      screen: "/knowledge/incidents",
      targets: ['[data-testid="report-incident-button"]'],
      title: "Report a problem",
      body: "If something is wrong at a machine, report it here. On your phone, choose More and add a photo to the report."
    }
  ],
  viewer: [
    {
      id: "vw-hub",
      route: "/operations",
      screen: "/operations",
      targets: HUB_TOP,
      title: "The Operations hub",
      body: "The plant at a glance: on-time delivery, today's slips and who is working. Select a figure to see the work behind it."
    },
    {
      id: "vw-menu",
      targets: ['[data-testid="menu-button"]', "#app-sidebar nav"],
      title: "What you can open",
      body: "Your menu lists every page you can read. Look around freely: opening a page never changes anything."
    }
  ],
  admin: [
    {
      id: "ad-users",
      route: "/admin/users-roles",
      screen: "/admin/users-roles",
      targets: ['[data-testid="people-table"]', '[data-testid="open-grant-dialog"]'],
      title: "Users and roles",
      body: "Give or remove a role for a person. Every grant is your signature, so you confirm it is you."
    },
    {
      id: "ad-golive",
      route: "/admin/communications",
      screen: "/admin/communications",
      targets: ['[data-testid="pilot-go-live-panel"]'],
      title: "Pilot go-live",
      body: "Switch each pilot feature on when people are ready, and see who receives what."
    },
    {
      id: "ad-comms",
      route: "/admin/communications",
      screen: "/admin/communications",
      targets: ['[data-testid="email-template-list"]'],
      title: "Communications",
      body: "Preview every email the application sends, and send yourself a test before anyone else gets one."
    },
    {
      id: "ad-sync",
      route: "/admin/data-sync",
      screen: "/admin/data-sync",
      targets: ['[data-testid="sync-state"]', '[data-testid="fabric-status-text"]'],
      title: "Data sync",
      body: "See when GSS was last read and whether it worked. A refresh outside the window needs a reason."
    },
    {
      id: "ad-ai",
      route: "/admin/ai-activity",
      screen: "/admin/ai-activity",
      targets: ['[data-testid="ai-usage-quotas"]', '[data-testid="ai-state"]'],
      title: "AI activity",
      body: "Every question to VIPER and its answer are recorded here, with the cost against the monthly budget."
    },
    {
      id: "ad-viewas",
      targets: ['[data-testid="user-menu"]'],
      title: "View as role",
      body: "Open your account menu and choose View as role to see the application exactly as a Purchaser, a Planner or any role sees it. Nothing can be changed while you view."
    }
  ]
};

const MORE_STEP: TourStep = {
  id: "more",
  kind: "more",
  targets: ['[data-testid="user-menu"]'],
  title: "More for your other roles",
  body: "You hold more than one role. Take the short part for another one now, or later from the account menu."
};

/** May this person see this step? Its page must be on their menu and its act within their roles. */
export function stepAllowed(step: TourStep, roles: readonly string[], screens: ReadonlySet<string>): boolean {
  if (step.screen && !screens.has(step.screen)) return false;
  if (step.roles && !hasAnyRole(roles, step.roles as readonly Role[])) return false;
  return true;
}

export interface BuiltTour {
  steps: TourStep[];
  primary: TourChapterId | null;
  others: TourChapterId[];
}

/**
 * The tour for these roles: the shared part, the lead chapter and, with several roles, the closing "More" card. With
 * `chapter`, only that chapter (from the "More" card). Steps the person's menu cannot open are left out.
 */
export function buildTour(roles: readonly string[], assignedRoles: readonly string[] | undefined, chapter: TourChapterId | null = null): BuiltTour {
  const screens = new Set<string>(screensFor(roles).map((s) => s.to));
  const allowed = (s: TourStep) => stepAllowed(s, roles, screens);
  const { primary, others } = selectChapters(assignedRoles && assignedRoles.length > 0 ? assignedRoles : roles);
  if (chapter) return { steps: CHAPTER_STEPS[chapter].filter(allowed), primary: chapter, others: [] };
  const steps = [...SHARED_STEPS.filter(allowed), ...(primary ? CHAPTER_STEPS[primary].filter(allowed) : [])];
  const offered = others.filter((c) => CHAPTER_STEPS[c].some(allowed));
  if (offered.length > 0) steps.push(MORE_STEP);
  return { steps, primary, others: offered };
}
